import React, { useMemo } from "react";
import { Bar } from "react-chartjs-2";

import { BarElement, CategoryScale, Chart, ChartData, ChartOptions, Legend, LinearScale, ScriptableContext, Tooltip } from "chart.js";

import { formatNumericCurrency } from "utils";

import { CurrencyCode } from "consts";

Chart.register(BarElement, CategoryScale, LinearScale, Legend, Tooltip);

type TimelineRow = {
  year: number;
  actual: number | null;
  eligible: number;
  nonEligible: number;
  unknown: boolean;
  isVerified: boolean;
};

type PayoutTimelineBarChartProps = {
  rows: TimelineRow[];
  currency?: CurrencyCode;
};

type PatternCacheContext = CanvasRenderingContext2D & {
  __payoutPatternCache?: Record<string, CanvasPattern | string>;
};

function getDiagonalPattern(
  ctx: CanvasRenderingContext2D,
  chartArea: { left: number; top: number } | undefined,
  cacheKey: string,
  strokeColor: string,
  backgroundColor: string
): CanvasPattern | string {
  const context = ctx as PatternCacheContext;
  const cache = context.__payoutPatternCache ?? {};
  if (cache[cacheKey]) return cache[cacheKey];

  const patternCanvas = document.createElement("canvas");
  patternCanvas.width = 24;
  patternCanvas.height = 24;
  const patternCtx = patternCanvas.getContext("2d");
  if (!patternCtx) return backgroundColor;

  patternCtx.fillStyle = backgroundColor;
  patternCtx.fillRect(0, 0, patternCanvas.width, patternCanvas.height);
  patternCtx.strokeStyle = strokeColor;
  patternCtx.lineWidth = 1.5;
  patternCtx.lineCap = "round";

  for (let x = -24; x <= 48; x += 8) {
    patternCtx.beginPath();
    patternCtx.moveTo(x, patternCanvas.height);
    patternCtx.lineTo(x + patternCanvas.width, 0);
    patternCtx.stroke();
  }

  const pattern = ctx.createPattern(patternCanvas, "repeat") ?? backgroundColor;
  if (typeof pattern !== "string" && pattern.setTransform) {
    pattern.setTransform(new DOMMatrix().translate(chartArea?.left ?? 0, chartArea?.top ?? 0));
  }

  cache[cacheKey] = pattern;
  context.__payoutPatternCache = cache;
  return pattern;
}

export const PayoutTimelineBarChart = ({ rows, currency }: PayoutTimelineBarChartProps) => {
  const maxStack = Math.max(1, ...rows.map((row) => (row.actual ?? 0) + row.eligible + row.nonEligible));
  const yMax = Math.max(12000, Math.ceil(maxStack / 2000) * 2000);
  const unknownBarValue = Math.round((yMax * 2) / 3);

  const labels = useMemo(() => rows.map((row) => String(row.year)), [rows]);

  const tickLines = useMemo(
    () =>
      rows.map((row) => {
        const amountLabel = row.unknown || row.actual === null ? "–" : formatNumericCurrency(Math.max(row.actual ?? 0, 0), { decimals: 0, currency });
        if (row.isVerified) return [String(row.year), amountLabel, "\u{1F512}"];
        return [String(row.year), amountLabel];
      }),
    [rows, currency]
  );

  const data: ChartData<"bar", number[], string> = {
    labels,
    datasets: [
      {
        label: "current",
        data: rows.map((row) => Math.max(row.actual ?? 0, 0)),
        stack: "payout",
        backgroundColor: "#4d85f5",
        borderRadius: 8,
        borderSkipped: false,
        barThickness: 52,
      },
      {
        label: "eligible",
        data: rows.map((row) => Math.max(row.eligible, 0)),
        stack: "payout",
        backgroundColor: "#b9d0fb",
        borderRadius: 8,
        borderSkipped: false,
        barThickness: 52,
      },
      {
        label: "nonEligible",
        data: rows.map((row) => (row.actual === null ? 0 : Math.max(row.nonEligible, 0))),
        stack: "payout",
        backgroundColor: (context: ScriptableContext<"bar">) =>
          getDiagonalPattern(context.chart.ctx, context.chart.chartArea, "nonEligible", "#b9d0fb", "#f1f6ff"),
        borderRadius: 8,
        borderSkipped: false,
        barThickness: 52,
      },
      {
        label: "unknown",
        data: rows.map((row) => (row.unknown || row.actual === null ? unknownBarValue : 0)),
        stack: "payout",
        backgroundColor: (context: ScriptableContext<"bar">) =>
          getDiagonalPattern(context.chart.ctx, context.chart.chartArea, "unknown", "rgba(153, 153, 153, 0.45)", "#fafafa"),
        borderRadius: 8,
        borderSkipped: false,
        barThickness: 52,
      },
    ],
  };

  const options: ChartOptions<"bar"> = {
    responsive: true,
    maintainAspectRatio: false,
    elements: {
      bar: {
        inflateAmount: 0,
      },
    },
    plugins: {
      // Global chartjs datalabels is enabled elsewhere in the app.
      // Explicitly disable it for this timeline.
      datalabels: {
        display: false,
      },
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
    },
    layout: {
      padding: {
        left: 0,
        right: 8,
        top: 0,
        bottom: 0,
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: {
          display: false,
        },
        border: {
          display: false,
        },
        ticks: {
          autoSkip: false,
          maxRotation: 0,
          minRotation: 0,
          align: "center",
          crossAlign: "near",
          callback: (_value, index) => tickLines[index] ?? "",
          color: "#6d6d6d",
          font: {
            family: "dazzed",
            size: 12,
            weight: 500,
            lineHeight: 1.5,
          },
          padding: 8,
        },
      },
      y: {
        stacked: true,
        min: 0,
        max: yMax,
        ticks: {
          stepSize: 2000,
          color: "#6d6d6d",
          font: {
            family: "dazzed",
            size: 10,
            weight: 500,
          },
          callback: (value: string | number) => {
            if (value === 0) return "0";
            return `${Math.round(Number(value) / 1000)}k`;
          },
          padding: 8,
        },
        grid: {
          color: "#d9d9d9",
          drawTicks: false,
        },
        border: {
          display: false,
        },
      },
    },
  };

  return <Bar data={data} options={options} data-testid="payout-timeline-chart" />;
};
